import React, { useState, useCallback, useRef } from 'react'
import _ from 'lodash'
import type { AuctionTransaction, Maybe } from '@adc/sdk/types'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import {
  searchTransactions,
  TransactionSearchFieldFragment,
  type TransactionSearchResult,
} from './searchTransactions'
import SearchField, { type ISearchFieldProps } from '../searchField'
import TransactionItem from './transactionItem'
import classnames from 'classnames/bind'
import styles from './styles.scss'

// @ts-ignore unsafe assignment of any
const cx = classnames.bind(styles)

const DEBOUNCE_WAIT_FOR_SEARCH_TYPING_MS = 500

type Selection = {
  key: string
  title: string
  description: string
  renderer: () => JSX.Element
  transaction: TransactionSearchResult
}

export interface ITransactionSearchFieldProps
  extends Partial<ISearchFieldProps> {
  placeholder?: string
  onChange: (value: string) => void
  onChangeCurrentTransaction: (
    transaction: Maybe<Partial<AuctionTransaction>>
  ) => void
  currentTransaction?: Maybe<Partial<AuctionTransaction>>
  onBlur?: () => void
  value: string
  viewMode?: boolean
}

interface TransactionSearchFieldType
  extends React.FC<ITransactionSearchFieldProps> {
  [GRAPH_API_FRAGMENT_DEFINITION]: string
  [GRAPH_API_FRAGMENT_REFERENCE]: string
}

/**
 * Extract the ID portion from a URN like "adc:transaction:abc123"
 */
const extractUrnId = (urn: string): string => {
  if (!urn) return ''
  const parts = urn.split(':')
  return parts.length >= 3 ? parts.slice(2).join(':') : urn
}

// @ts-ignore next line
const TransactionSearchField: TransactionSearchFieldType = ({
  onChange,
  onBlur, // eslint-disable-line @typescript-eslint/no-unused-vars
  placeholder = 'Search by Transaction ID, URN, Listing ID, or address',
  viewMode,
  onChangeCurrentTransaction,
  currentTransaction,
  ...rest
}) => {
  const [results, setResults] = useState<TransactionSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const fetchCounter = useRef(0)

  const fetchTransactions = useCallback(
    async (searchTerm: string, currentFetchId: number) => {
      const results = await searchTransactions(searchTerm)
      if (currentFetchId === fetchCounter.current) {
        const content = (results || []).filter(
          Boolean
        ) as TransactionSearchResult[]
        setResults(content)
      }
    },
    []
  )

  const debouncedFetchTransactions = useRef(
    _.debounce(async (value: string) => {
      setLoading(true)
      const currentFetchId = ++fetchCounter.current
      try {
        await fetchTransactions(value, currentFetchId)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('TransactionSearchField: search failed', e)
        if (currentFetchId === fetchCounter.current) {
          setResults([])
        }
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_WAIT_FOR_SEARCH_TYPING_MS)
  ).current

  const onSearchChange = useCallback(
    async (_e: unknown, { value }: { value: string }) => {
      await debouncedFetchTransactions(value)
    },
    [debouncedFetchTransactions]
  )

  const resultRenderer = (transaction: TransactionSearchResult) => (
    <TransactionItem
      elmId='testing'
      transaction={transaction as Maybe<Partial<AuctionTransaction>>}
    />
  )

  const onResultSelect = (_e: unknown, data: { result: Selection }) => {
    onChangeCurrentTransaction(
      data.result.transaction as Maybe<Partial<AuctionTransaction>>
    )
    onChange(data.result.key || '')
  }

  const resultsMapped = results.map(result => ({
    key: result?.transaction_id,
    title: extractUrnId(result?.transaction_urn || ''),
    description: result?.transaction_id,
    renderer: () => resultRenderer(result),
    transaction: result,
  }))

  return (
    <div
      className={cx('container', {
        expanded: currentTransaction,
        'has-label': !!rest.label,
      })}
    >
      <SearchField
        results={resultsMapped}
        onSearchChange={onSearchChange}
        onResultSelect={onResultSelect}
        noResultsMessage='No results found'
        selectFirstResult
        showNoResults
        className={cx('transaction-search-field')}
        loading={loading}
        placeholder={placeholder}
        viewMode={viewMode}
        borderBottom='none'
        onChange={onChange}
        {...rest}
      />
      <div
        className={cx({
          'transaction-item-cover-mode': currentTransaction,
          'has-label': !!rest.label,
        })}
      >
        <TransactionItem
          elmId='transaction_item'
          transaction={currentTransaction || null}
          onClose={() => {
            onChangeCurrentTransaction(null)
            onChange('')
          }}
          viewMode={viewMode}
        />
      </div>
    </div>
  )
}

TransactionSearchField[GRAPH_API_FRAGMENT_DEFINITION] =
  TransactionSearchFieldFragment
TransactionSearchField[
  GRAPH_API_FRAGMENT_REFERENCE
] = `...TransactionSearchFields`

export default TransactionSearchField
